/**
 * Editor de arrastrar y soltar (PLAN_SUTEX_Y_PLANTILLAS F1c): paleta, lienzo y
 * propiedades sobre la misma definición que guarda la API.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent as KeyboardEventReact, type ReactNode } from 'react'
import {
  AlignCenterHorizontal, AlignCenterVertical, AlignEndHorizontal, AlignEndVertical,
  AlignHorizontalDistributeCenter, AlignStartHorizontal, AlignStartVertical, AlignVerticalDistributeCenter,
  ChevronDown, Copy, LayoutGrid, Redo2, StretchHorizontal, StretchVertical, Trash2, Undo2,
} from 'lucide-react'
import { Button } from '@/components/ds/Button'
import { CheckboxField } from '@/components/ds/CheckboxField'
import { IconButton } from '@/components/ds/IconButton'
import { SegmentGroup } from '@/components/ds/SegmentGroup'
import { SelectField } from '@/components/ds/SelectField'
import { useCierreAlClicAfuera } from '@/lib/useCierreAlClicAfuera'
import {
  fetchCanvasData,
  usePrintTemplatePalette,
  type DatosDelElemento,
  type PreviewVariant,
} from '@/modules/config/hooks/usePrintTemplates'
import {
  TEXTO_MONTO_EN_PALABRAS, acotar, acotarCorrimiento, adaptarAlPapel, admiteIgualar, alinear, igualarMedida, altoDelLienzo, bloqueAcuseDeRecibo,
  cajaDelGrupo, camposDeLaLista, conAltoDeLista, desplazar, dimensiones, distribuir, elementoDeCampo, elementoNuevo,
  nuevoId, vaEnLaCopia, visibleEnVista, type Alineacion, type Copia,
  type CampoArrastrado, type Definicion, type Elemento, type TipoElemento, type Vista,
} from './geometria'
import { ControlesDePapel } from './ControlesDePapel'
import { Lienzo } from './Lienzo'
import { Paleta } from './Paleta'
import { Propiedades, PropiedadesDeLaHoja } from './Propiedades'

const VISTAS: { value: Vista; label: string; hint: string }[] = [
  { value: 'first', label: 'Primera', hint: 'Lo que lleva la primera página' },
  { value: 'next', label: 'Siguientes', hint: 'Las páginas de continuación del detalle' },
  { value: 'last', label: 'Última', hint: 'Totales, timbre y lo que va al final' },
]
const ZOOMS = [0.5, 0.75, 1, 1.25, 1.5]
type Zoom = number | 'ajustar'
const MAX_HISTORIAL = 100
/** Cambios seguidos del mismo elemento (tecleo en propiedades) cuentan como uno al deshacer. */
const AGRUPAR_MS = 800

const ALINEACIONES: { como: Alineacion; label: string; icon: ReactNode }[] = [
  { como: 'izquierda', label: 'Alinear a la izquierda', icon: <AlignStartVertical size={15} /> },
  { como: 'centro', label: 'Centrar en horizontal', icon: <AlignCenterVertical size={15} /> },
  { como: 'derecha', label: 'Alinear a la derecha', icon: <AlignEndVertical size={15} /> },
  { como: 'arriba', label: 'Alinear arriba', icon: <AlignStartHorizontal size={15} /> },
  { como: 'medio', label: 'Centrar en vertical', icon: <AlignCenterHorizontal size={15} /> },
  { como: 'abajo', label: 'Alinear abajo', icon: <AlignEndHorizontal size={15} /> },
]

const ultimoSegmento = (ruta: string) => ruta.split('/').pop() ?? ruta

function esCampoDeTexto(t: EventTarget | null) {
  const el = t as HTMLElement | null
  return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable)
}

export function EditorVisual({ definicion, onChange, tipos, editable, variante, onVariante, onErrores }: {
  definicion: Definicion
  onChange: (d: Definicion) => void
  tipos: number[]
  editable: boolean
  variante: PreviewVariant
  onVariante: (v: PreviewVariant) => void
  onErrores: (e: Record<string, string[]>) => void
}) {
  const [seleccion, setSeleccion] = useState<string[]>([])
  const [vista, setVista] = useState<Vista>('first')
  const [zoom, setZoom] = useState<Zoom>('ajustar')
  const [verRutas, setVerRutas] = useState(false)
  const [copia, setCopia] = useState<Copia>('original')
  const [datos, setDatos] = useState<Record<string, DatosDelElemento>>({})
  const [pasado, setPasado] = useState<Definicion[]>([])
  const [futuro, setFuturo] = useState<Definicion[]>([])
  const [aviso, setAviso] = useState('')
  const [menuAbierto, setMenuAbierto] = useState(false)
  const antesDelArrastre = useRef<Definicion | null>(null)
  const ultimoRegistro = useRef<{ id: string | null; t: number }>({ id: null, t: 0 })
  const copiado = useRef<Elemento[]>([])
  const pedido = useRef(0)

  const { ancho, alto } = dimensiones(definicion.paper)
  const enPantalla = useCallback((el: Elemento) => visibleEnVista(el, vista) && vaEnLaCopia(el, copia), [vista, copia])
  // En el orden de la definición; un id que ya no existe (tras deshacer) queda fuera solo.
  const elegidos = definicion.elements.filter(e => seleccion.includes(e.id))
  const elegido = elegidos.length === 1 ? elegidos[0] : null
  // La misma consulta que la paleta (queda en caché): los campos de las columnas.
  const { data: paleta } = usePrintTemplatePalette(variante, tipos)
  const camposDelDetalle = useMemo(() => camposDeLaLista(paleta?.tree ?? [], 'Detalle'), [paleta])
  const camposDeReferencias = useMemo(() => camposDeLaLista(paleta?.tree ?? [], 'Referencia'), [paleta])

  // Lo que deja de verse al cambiar de página o de copia sale de la selección:
  // las flechas no deben mover algo que no está en pantalla.
  useEffect(() => {
    setSeleccion(s => s.filter(id => {
      const el = definicion.elements.find(e => e.id === id)
      return !!el && enPantalla(el)
    }))
    // Solo al cambiar la vista: un elemento recién agregado ya es visible.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enPantalla])

  // ─── Historial ─────────────────────────────────────────────────────────────
  const aplicar = useCallback((nueva: Definicion, agruparCon: string | null = null) => {
    if (!editable) return
    const ahora = Date.now()
    const agrupar = agruparCon !== null && ultimoRegistro.current.id === agruparCon && ahora - ultimoRegistro.current.t < AGRUPAR_MS
    if (!agrupar) {
      setPasado(p => [...p.slice(-MAX_HISTORIAL + 1), definicion])
      setFuturo([])
    }
    ultimoRegistro.current = { id: agruparCon, t: ahora }
    onChange(nueva)
  }, [definicion, editable, onChange])

  const conElementos = (els: Elemento[]) => {
    const nuevos = new Map(els.map(e => [e.id, e]))
    return { ...definicion, elements: definicion.elements.map(e => nuevos.get(e.id) ?? e) }
  }
  const conElemento = (el: Elemento) => conElementos([el])

  const deshacer = useCallback(() => {
    if (!pasado.length) return
    setFuturo(f => [definicion, ...f])
    onChange(pasado[pasado.length - 1])
    setPasado(p => p.slice(0, -1))
  }, [pasado, definicion, onChange])

  const rehacer = useCallback(() => {
    if (!futuro.length) return
    setPasado(p => [...p, definicion])
    onChange(futuro[0])
    setFuturo(f => f.slice(1))
  }, [futuro, definicion, onChange])

  // ─── Datos del lienzo (mismos formatos y condiciones que el PDF) ───────────
  useEffect(() => {
    const id = ++pedido.current
    const espera = setTimeout(() => {
      fetchCanvasData(definicion, variante, copia)
        .then(r => {
          if (id !== pedido.current) return
          setDatos(r.elements)
          onErrores(r.errors)
        })
        .catch(() => { /* el lienzo sigue con las rutas; la vista previa mostrará el error */ })
    }, 400)
    return () => clearTimeout(espera)
  }, [definicion, variante, copia, onErrores])

  // ─── Agregar y soltar ──────────────────────────────────────────────────────
  const agregar = (el: Elemento) => {
    aplicar({ ...definicion, elements: [...definicion.elements, el] })
    setSeleccion([el.id])
  }

  const soltarElemento = (tipo: string, x: number, y: number) => {
    if (tipo === 'acuse_de_recibo') {
      const bloque = bloqueAcuseDeRecibo(x, y, definicion)
      aplicar({ ...definicion, elements: [...definicion.elements, ...bloque] })
      // Queda elegido entero: se mueve o se alinea como un solo bloque.
      setSeleccion(bloque.map(e => e.id))
      // Va solo en la cedible y en la última página: el lienzo pasa a esa vista para verlo.
      setCopia('cedible')
      setVista('last')
      setAviso('El acuse de recibo va en la última página de la copia cedible: el lienzo pasó a mostrar esa vista.')
      return
    }
    if (tipo === 'monto_en_palabras') {
      // Un texto ya armado: se calcula del total del documento y queda editable.
      agregar(acotar({
        id: nuevoId('monto-en-palabras', definicion.elements), type: 'text', x_mm: x, y_mm: y, w_mm: 120, h_mm: 10,
        text: TEXTO_MONTO_EN_PALABRAS, style: { font_size_pt: 8 },
      }, ancho, alto))
      return
    }
    if (tipo === 'detail_table') {
      const existente = definicion.elements.find(e => e.type === 'detail_table')
      if (existente) {
        setSeleccion([existente.id])
        setAviso('Una plantilla lleva una sola tabla de detalle: queda elegida la que ya existe.')
        return
      }
    }
    agregar(elementoNuevo(tipo as TipoElemento, x, y, definicion))
  }

  const soltarCampo = (campo: CampoArrastrado, x: number, y: number, sobre: string | null) => {
    const destino = definicion.elements.find(e => e.id === sobre)
    const listaDelDestino = destino?.type === 'detail_table' ? 'Detalle' : destino?.type === 'references_table' ? 'Referencia' : null
    if (destino && listaDelDestino && campo.lista === listaDelDestino) {
      const columnas = [...(destino.columns ?? []), { header: ultimoSegmento(campo.rutaEnLaLista), path: campo.rutaEnLaLista, width_pct: 15 }]
      aplicar(conElemento({ ...destino, columns: columnas }))
      setSeleccion([destino.id])
      return
    }
    if (destino?.type === 'field_list' && !campo.lista) {
      // Una fila más: el rótulo sale del nombre del campo y se corrige después.
      const filas = [...(destino.rows ?? []), { label: `${ultimoSegmento(campo.ruta)}:`, value: `{${campo.ruta}}` }]
      aplicar(conElemento(acotar(conAltoDeLista({ ...destino, rows: filas }), ancho, alto)))
      setSeleccion([destino.id])
      return
    }
    if (destino?.type === 'text') {
      aplicar(conElemento({ ...destino, text: `${destino.text ?? ''}${destino.text ? ' ' : ''}{${campo.ruta}}` }))
      setSeleccion([destino.id])
      return
    }
    agregar(elementoDeCampo(campo.ruta, x, y, definicion))
  }

  const borrar = useCallback(() => {
    if (!seleccion.length) return
    aplicar({ ...definicion, elements: definicion.elements.filter(e => !seleccion.includes(e.id)) })
    setSeleccion([])
  }, [seleccion, definicion, aplicar])

  /** Copias corridas 5 mm, con ids nuevos; la tabla del detalle no se duplica (hay una sola). */
  const duplicar = (els: Elemento[] = elegidos) => {
    const creados: Elemento[] = []
    for (const el of els) {
      if (el.type === 'detail_table') continue
      const id = nuevoId(el.type.replace('_', '-'), [...definicion.elements, ...creados])
      creados.push(acotar({ ...el, id, x_mm: el.x_mm + 5, y_mm: el.y_mm + 5 }, ancho, alto))
    }
    if (!creados.length) return
    aplicar({ ...definicion, elements: [...definicion.elements, ...creados] })
    setSeleccion(creados.map(e => e.id))
  }

  /** Corre la selección; si choca con el borde de la hoja se detiene entera. */
  const correr = (dx: number, dy: number) => {
    const c = acotarCorrimiento(cajaDelGrupo(elegidos, vista), dx, dy, ancho, alto)
    aplicar(conElementos(elegidos.map(el => acotar(desplazar(el, c.dx, c.dy), ancho, alto))), `teclado:${seleccion.join(',')}`)
  }

  const alinearSeleccion = (como: Alineacion) => {
    const pagina = { x: 0, y: 0, w: ancho, h: altoDelLienzo(definicion) }
    aplicar(conElementos(alinear(elegidos, como, vista, pagina).map(el => acotar(el, ancho, alto))))
  }

  const distribuirSeleccion = (eje: 'x' | 'y') => {
    aplicar(conElementos(distribuir(elegidos, eje, vista).map(el => acotar(el, ancho, alto))))
  }

  // El timbre queda dentro de las medidas del SII aunque el más grande las pase.
  const igualarSeleccion = (medida: 'w' | 'h') => {
    aplicar(conElementos(igualarMedida(elegidos, medida).map(el => acotar(el, ancho, alto))))
  }
  const puedeIgualar = (medida: 'w' | 'h') => editable && elegidos.filter(el => admiteIgualar(el, medida)).length >= 2

  // ─── Teclado ───────────────────────────────────────────────────────────────
  useEffect(() => {
    const alTeclear = (e: KeyboardEvent) => {
      if (esCampoDeTexto(e.target)) return
      const mod = e.ctrlKey || e.metaKey
      if (mod && e.key.toLowerCase() === 'z') { e.preventDefault(); if (e.shiftKey) rehacer(); else deshacer(); return }
      if (mod && e.key.toLowerCase() === 'y') { e.preventDefault(); rehacer(); return }
      // Con el menú abierto, Esc solo lo cierra: el menú corta el evento si el foco está adentro.
      if (e.key === 'Escape' && seleccion.length && !menuAbierto) { setSeleccion([]); return }
      if (mod && e.key.toLowerCase() === 'a') {
        e.preventDefault()
        setSeleccion(definicion.elements.filter(enPantalla).map(el => el.id))
        return
      }
      if (!editable) return
      if (mod && e.key.toLowerCase() === 'c' && elegidos.length) { copiado.current = elegidos; return }
      if (mod && e.key.toLowerCase() === 'v' && copiado.current.length) { e.preventDefault(); duplicar(copiado.current); return }
      if (!elegidos.length) return
      if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); borrar(); return }
      const paso = e.shiftKey ? 5 : 1
      const delta = { ArrowLeft: [-paso, 0], ArrowRight: [paso, 0], ArrowUp: [0, -paso], ArrowDown: [0, paso] }[e.key]
      if (!delta) return
      e.preventDefault()
      correr(delta[0], delta[1])
    }
    window.addEventListener('keydown', alTeclear)
    return () => window.removeEventListener('keydown', alTeclear)
  })

  useEffect(() => { if (!aviso) return; const t = setTimeout(() => setAviso(''), 4000); return () => clearTimeout(t) }, [aviso])

  const sinSeleccion = !editable || !elegidos.length

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-end gap-3">
        <ControlesDePapel compacto papel={definicion.paper} disabled={!editable}
          onChange={p => aplicar(adaptarAlPapel(definicion, p))} />
        <SegmentGroup label="Página" size="sm" value={vista} onChange={setVista} options={VISTAS} />
        <SegmentGroup label="Copia" size="sm" value={copia} onChange={setCopia} options={[
          { value: 'original', label: 'Original' },
          { value: 'cedible', label: 'Cedible', hint: 'La copia que se entrega al cederla (factoring)' },
        ]} />
        <SelectField dense label="Zoom" value={String(zoom)} className="w-40"
          onChange={e => setZoom(e.target.value === 'ajustar' ? 'ajustar' : Number(e.target.value))}>
          <option value="ajustar">Ajustar al ancho</option>
          {ZOOMS.map(z => <option key={z} value={z}>{Math.round(z * 100)} %</option>)}
        </SelectField>
        <CheckboxField label="Ver rutas" checked={verRutas} onChange={e => setVerRutas(e.target.checked)} />
        <div className="flex items-center gap-1 ml-auto">
          <MenuOrganizar abierto={menuAbierto} onAbierto={setMenuAbierto} disabled={sinSeleccion} secciones={[
            {
              titulo: elegidos.length === 1 ? 'Alinear a la página' : 'Alinear entre sí',
              opciones: ALINEACIONES.map(a => ({ label: a.label, icon: a.icon, onClick: () => alinearSeleccion(a.como) })),
            },
            {
              titulo: 'Distribuir (3 o más)',
              opciones: [
                { label: 'Distribuir en horizontal', icon: <AlignHorizontalDistributeCenter size={15} />,
                  disabled: elegidos.length < 3, onClick: () => distribuirSeleccion('x') },
                { label: 'Distribuir en vertical', icon: <AlignVerticalDistributeCenter size={15} />,
                  disabled: elegidos.length < 3, onClick: () => distribuirSeleccion('y') },
              ],
            },
            {
              titulo: 'Igualar al más grande',
              opciones: [
                { label: 'Igualar el ancho', icon: <StretchHorizontal size={15} />, disabled: !puedeIgualar('w'), onClick: () => igualarSeleccion('w') },
                { label: 'Igualar el alto', icon: <StretchVertical size={15} />, disabled: !puedeIgualar('h'), onClick: () => igualarSeleccion('h') },
              ],
            },
          ]} />
          <IconButton label="Deshacer (Ctrl+Z)" icon={<Undo2 size={15} />} disabled={!pasado.length} onClick={deshacer} />
          <IconButton label="Rehacer (Ctrl+Y)" icon={<Redo2 size={15} />} disabled={!futuro.length} onClick={rehacer} />
        </div>
      </div>
      {aviso && <p role="status" className="text-xs text-warning-content">{aviso}</p>}

      <div className="hidden min-[1280px]:grid grid-cols-[250px_minmax(0,1fr)_300px] gap-4 h-[calc(100vh-300px)] min-h-[560px]">
        <aside className="min-h-0 overflow-hidden">
          {editable ? (
            <Paleta variante={variante} onVariante={onVariante} tipos={tipos}
              onAgregarCampo={c => agregar(elementoDeCampo(c.ruta, 10, 10, definicion))}
              onAgregarElemento={t => (t === 'acuse_de_recibo'
                // Con un clic, el acuse va donde siempre: abajo a la derecha.
                ? soltarElemento(t, ancho - 90, (alto ?? 279.4) - 50)
                : soltarElemento(t, 10, 10))} />
          ) : (
            <p className="text-xs text-content-subtle p-2">Versión en solo lectura: la paleta se activa en el borrador.</p>
          )}
        </aside>
        <Lienzo
          definicion={definicion} vista={vista} copia={copia} zoom={zoom} seleccion={seleccion} datos={datos} verRutas={verRutas}
          onSeleccionar={setSeleccion}
          onCambiar={els => {
            if (!editable) return
            if (!antesDelArrastre.current) antesDelArrastre.current = definicion
            onChange(conElementos(els))
          }}
          onConfirmar={() => {
            if (antesDelArrastre.current) {
              const antes = antesDelArrastre.current
              setPasado(p => [...p.slice(-MAX_HISTORIAL + 1), antes])
              setFuturo([])
              antesDelArrastre.current = null
            }
          }}
          onSoltarCampo={editable ? soltarCampo : () => undefined}
          onSoltarElemento={editable ? soltarElemento : () => undefined}
        />
        <aside className="min-h-0 overflow-y-auto pr-1">
          <fieldset disabled={!editable}>
            {elegidos.length > 1 ? (
              <PropiedadesDelGrupo cantidad={elegidos.length} onBorrar={borrar} onDuplicar={() => duplicar()} />
            ) : elegido ? (
              <Propiedades el={elegido} datos={datos[elegido.id]}
                camposDelDetalle={camposDelDetalle} camposDeReferencias={camposDeReferencias}
                onChange={el => aplicar(conElemento(el), el.id)} onBorrar={borrar} onDuplicar={() => duplicar()} />
            ) : (
              <PropiedadesDeLaHoja definicion={definicion} onChange={aplicar} />
            )}
          </fieldset>
        </aside>
      </div>
      <p className="min-[1280px]:hidden text-sm text-content-muted bg-surface-muted border border-divider rounded-lg p-4">
        El editor necesita una pantalla de al menos 1280 px de ancho. Mientras tanto, la vista previa y la pestaña Avanzado siguen disponibles.
      </p>
    </div>
  )
}

interface OpcionDelMenu {
  label: string
  icon: ReactNode
  disabled?: boolean
  onClick: () => void
}

/**
 * Alinear, distribuir e igualar en un solo botón: en la barra entran sin
 * pasarla a una segunda fila, y lo que más se usa (alinear) va primero.
 */
function MenuOrganizar({ abierto, onAbierto, disabled, secciones }: {
  abierto: boolean
  onAbierto: (v: boolean) => void
  disabled: boolean
  secciones: { titulo: string; opciones: OpcionDelMenu[] }[]
}) {
  const disparador = useRef<HTMLDivElement>(null)
  const panel = useRef<HTMLDivElement>(null)
  useCierreAlClicAfuera(abierto, [disparador, panel], () => onAbierto(false))
  useEffect(() => { if (disabled) onAbierto(false) }, [disabled, onAbierto])
  // Al abrir, el foco va a la primera opción disponible, para usarlo con el teclado.
  useEffect(() => {
    if (abierto) panel.current?.querySelector<HTMLButtonElement>('button:not([disabled])')?.focus()
  }, [abierto])

  const moverFoco = (e: KeyboardEventReact<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      // Se corta aquí: si llegara al editor, además de cerrar el menú vaciaría la selección.
      e.stopPropagation()
      onAbierto(false)
      disparador.current?.querySelector('button')?.focus()
      return
    }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
    // Las flechas recorren el menú; no deben mover los elementos del lienzo.
    e.preventDefault()
    e.stopPropagation()
    const botones = [...(panel.current?.querySelectorAll<HTMLButtonElement>('button:not([disabled])') ?? [])]
    const i = botones.indexOf(document.activeElement as HTMLButtonElement)
    botones[(i + (e.key === 'ArrowDown' ? 1 : botones.length - 1)) % botones.length]?.focus()
  }

  return (
    <div className="relative" ref={disparador}>
      <Button size="sm" icon={<LayoutGrid size={14} />} iconRight={<ChevronDown size={14} />} disabled={disabled}
        aria-haspopup="menu" aria-expanded={abierto}
        title={disabled ? 'Elegir uno o más elementos del lienzo' : undefined}
        onClick={() => onAbierto(!abierto)}>
        Organizar
      </Button>
      {abierto && (
        <div ref={panel} role="menu" aria-label="Organizar" onKeyDownCapture={moverFoco}
          className="absolute right-0 top-full mt-1 z-30 w-60 rounded-lg border border-divider bg-surface shadow-card p-1">
          {secciones.map(s => (
            <div key={s.titulo} className="py-1 border-b border-divider last:border-b-0">
              <p className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-content-subtle">{s.titulo}</p>
              {s.opciones.map(o => (
                <button key={o.label} type="button" role="menuitem" disabled={o.disabled}
                  onClick={() => { o.onClick(); onAbierto(false) }}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm text-content hover:bg-surface-muted focus-visible:bg-surface-muted focus-visible:outline-none disabled:opacity-40 disabled:hover:bg-transparent">
                  <span className="text-content-muted">{o.icon}</span>
                  {o.label}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/** Con varios elegidos no se mezclan propiedades: se mueven, alinean, duplican o borran juntos. */
function PropiedadesDelGrupo({ cantidad, onBorrar, onDuplicar }: {
  cantidad: number; onBorrar: () => void; onDuplicar: () => void
}) {
  return (
    <div className="space-y-3 text-sm">
      <div className="flex items-center justify-between gap-2">
        <p className="font-semibold text-content">{cantidad} elementos elegidos</p>
        <div className="flex gap-1">
          <IconButton label="Duplicar los elegidos" icon={<Copy size={14} />} bare onClick={onDuplicar} />
          <IconButton label="Borrar los elegidos" icon={<Trash2 size={14} />} bare onClick={onBorrar} />
        </div>
      </div>
      <div className="text-xs text-content-subtle space-y-2">
        <p>Arrastrar cualquiera de ellos mueve el grupo, y las flechas también. Alinear, distribuir e igualar están en «Organizar», sobre el lienzo.</p>
        <p>Shift+clic suma o quita un elemento; Esc deja la selección vacía. Para editar las propiedades, elegir uno solo.</p>
      </div>
    </div>
  )
}
